import type { PreparedBol } from "@src/types/api";
import type { ShipmentDraft, DraftItem } from "@src/load-agent/domain/shipmentDraft";
import { parseTimeForInput } from "@taskpane/utils/dateUtils";

interface PreparedBolFormMapping {
  formKey: keyof ShipmentDraft["form"];
  bolKey: keyof PreparedBol;
  transform?: (v: string) => string;
}
const FIELD_MAPPINGS: PreparedBolFormMapping[] = [
  { formKey: "originAddress", bolKey: "shipperAddress" },
  { formKey: "originAddress2", bolKey: "shipperAddress2" },
  { formKey: "destinationAddress", bolKey: "consigneeAddress" },
  { formKey: "destinationAddress2", bolKey: "consigneeAddress2" },
  { formKey: "shipperName", bolKey: "shipperName" },
  { formKey: "consigneeName", bolKey: "consigneeName" },
  { formKey: "shipperContact", bolKey: "shipperContact" },
  { formKey: "shipperPhone", bolKey: "shipperPhone" },
  { formKey: "consigneeContact", bolKey: "consigneeContact" },
  { formKey: "consigneePhone", bolKey: "consigneePhone" },
  { formKey: "shipperNumber", bolKey: "shipperNumber" },
  { formKey: "poNumber", bolKey: "poReference" },
  { formKey: "billOfLadingNote", bolKey: "billOfLadingNote" },
  { formKey: "pickupOpenTime", bolKey: "pickupOpenTime", transform: parseTimeForInput },
  { formKey: "pickupCloseTime", bolKey: "pickupCloseTime", transform: parseTimeForInput },
];

function isEmpty(val: string | undefined | null): boolean {
  return !val || val.trim() === "";
}

export function applyPreparedBol(
  draft: ShipmentDraft,
  preparedBol: PreparedBol | null | undefined
): ShipmentDraft {
  if (!preparedBol) return draft;
  const form = { ...draft.form };

  for (const { formKey, bolKey, transform } of FIELD_MAPPINGS) {
    const raw = preparedBol[bolKey];

    if (raw == null || typeof raw === "object") continue;
    const value = transform ? transform(String(raw)) : String(raw);

    if (!isEmpty(value) && isEmpty((draft.form as Record<string, string>)[formKey])) {
      (form as Record<string, string>)[formKey] = value;
    }
  }
  const bolItems = preparedBol.items;
  let items = draft.items;

  if (Array.isArray(bolItems) && bolItems.length > 0) {
    items = draft.items.map((item: DraftItem, idx: number): DraftItem => {
      if (item.nmfc) return item;
      const bolItem = bolItems[idx];
      const nmfc = bolItem?.nmfc;

      if (nmfc == null) return item;
      return { ...item, nmfc: String(nmfc) };
    });
  }
  return { ...draft, form, items };
}
